import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DroneInventory {
  id: string;
  name: string;
  manufacturerId: string;
  modelId: string;
  serialNumber: string;
  packagingSerialNumber: string | null;
  version: string | null;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  // Registration fields
  nNumber: string | null;
  nNumberActiveDate: string | null;
  remoteId: string | null;
  remoteBroadcastModule: string | null;
  // Joined fields
  manufacturerName?: string;
  modelName?: string;
  createdAt: string;
  updatedAt: string;
  // Zoho FSM sync fields (for Service Appointments/Assets)
  zohoFsmProductId: string | null;
  zohoFsmSyncStatus: 'pending' | 'synced' | 'error' | null;
  zohoFsmSyncedAt: string | null;
  zohoFsmSyncError: string | null;
  // Zoho Desk sync fields (for Support Tickets)
  zohoDeskProductId: string | null;
  zohoDeskSyncStatus: 'pending' | 'synced' | 'error' | null;
  zohoDeskSyncedAt: string | null;
  zohoDeskSyncError: string | null;
}

interface Manufacturer {
  id: string;
  name: string;
}

@Component({
  selector: 'app-drones',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './drones.component.html',
  styleUrl: './drones.component.scss',
})
export class DronesComponent implements OnInit {
  drones: DroneInventory[] = [];
  allDrones: DroneInventory[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalDrones = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterStatus: 'all' | 'available' | 'assigned' | 'maintenance' | 'retired' = 'all';
  filterManufacturer: string = 'all';
  sortBy: 'createdAt' | 'name' | 'serialNumber' = 'createdAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Available manufacturers for filter dropdown
  manufacturers: Manufacturer[] = [];

  constructor(
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadManufacturers();
    this.loadDrones();
  }

  async loadManufacturers(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: Manufacturer[] }>(
          `${environment.apiUrl}/admin/drone-manufacturers`
        )
      );

      if (response.success && response.data) {
        this.manufacturers = response.data;
      }
    } catch (error: any) {
      console.error('Failed to load manufacturers:', error);
    }
  }

  async loadDrones(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // Build query params — all filters applied together (AND logic)
      const params = new URLSearchParams();
      if (this.filterStatus !== 'all') {
        params.set('status', this.filterStatus);
      }
      if (this.filterManufacturer !== 'all') {
        params.set('manufacturerId', this.filterManufacturer);
      }
      const trimmedSearch = this.searchQuery.trim();
      if (trimmedSearch) {
        params.set('search', trimmedSearch);
      }
      const queryString = params.toString();
      const url = `${environment.apiUrl}/admin/drones${queryString ? '?' + queryString : ''}`;

      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneInventory[] }>(url)
      );

      if (response.success && response.data) {
        this.allDrones = response.data;

        // Apply client-side filtering and sorting
        this.applySortingAndPagination();
      }
    } catch (error: any) {
      console.error('Load drones error:', error);
      this.errorMessage = 'Failed to load drones';
    } finally {
      this.loading = false;
    }
  }

  private applySortingAndPagination(): void {
    let filteredDrones = [...this.allDrones];

    // Apply sorting
    filteredDrones.sort((a, b) => {
      let comparison = 0;
      switch (this.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'serialNumber':
          comparison = a.serialNumber.localeCompare(b.serialNumber);
          break;
        case 'createdAt':
        default:
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return this.sortOrder === 'desc' ? -comparison : comparison;
    });

    this.totalDrones = filteredDrones.length;
    this.totalPages = Math.ceil(this.totalDrones / this.pageSize);

    // Apply pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    this.drones = filteredDrones.slice(startIndex, startIndex + this.pageSize);
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadDrones();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadDrones();
  }
  
  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterStatus !== 'all' || this.filterManufacturer !== 'all' || this.sortBy !== 'createdAt' || this.sortOrder !== 'desc');
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterStatus = 'all';
    this.filterManufacturer = 'all';
    this.sortBy = 'createdAt';
    this.sortOrder = 'desc';
    this.onFilterChange();
  }

  onSortChange(): void {
    this.applySortingAndPagination();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.applySortingAndPagination();
    }
  }

  viewDrone(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'details']);
  }

  viewDroneDetails(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'details']);
  }

  editDrone(droneId: string): void {
    this.router.navigate(['/drones', droneId, 'edit']);
  }

  createDrone(): void {
    this.router.navigate(['/drones/create']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusClass(status: DroneInventory['status']): string {
    switch (status) {
      case 'available': return 'bg-success';
      case 'assigned': return 'bg-primary';
      case 'maintenance': return 'bg-warning';
      case 'retired': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: DroneInventory['status']): string {
    switch (status) {
      case 'available': return 'Available';
      case 'assigned': return 'Assigned';
      case 'maintenance': return 'Maintenance';
      case 'retired': return 'Retired';
      default: return status;
    }
  }

  getZohoSyncStatusClass(syncStatus: DroneInventory['zohoFsmSyncStatus']): string {
    switch (syncStatus) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-danger';
      default: return 'text-muted';
    }
  }

  getZohoSyncStatusIcon(syncStatus: DroneInventory['zohoFsmSyncStatus']): string {
    switch (syncStatus) {
      case 'synced': return 'bi-cloud-check-fill';
      case 'pending': return 'bi-cloud-arrow-up';
      case 'error': return 'bi-cloud-slash-fill';
      default: return 'bi-cloud';
    }
  }

  getZohoSyncStatusTooltip(drone: DroneInventory): string {
    switch (drone.zohoFsmSyncStatus) {
      case 'synced':
        return drone.zohoFsmSyncedAt
          ? `Synced to Zoho FSM on ${this.formatDate(drone.zohoFsmSyncedAt)}`
          : 'Synced to Zoho FSM';
      case 'pending': return 'Pending sync to Zoho FSM';
      case 'error': return drone.zohoFsmSyncError || 'FSM sync error';
      default: return 'Not synced to Zoho FSM';
    }
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // Expose Math for template
  readonly Math = Math;
}
